import { useEffect, useId, useRef } from "react";

import { formatPoints } from "../lib/format";
import {
  GENERATION_KIND_META,
  estimatedUnitPoints,
  formatEstimateDefaults,
  type PendingGeneration,
} from "../lib/generations";

export interface EstimateDialogProps {
  pending: PendingGeneration;
  /** `null` = 余额还没取到（不阻塞，但会提示风险）。 */
  balance: number | null;
  submitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * 「花钱前先看一眼」确认层。
 *
 * 必显：预估总点数、单价、当前余额。余额不足时**禁用**确认按钮，
 * 不让用户点了才收到 402。所有数字都来自 `POST /generations/estimate`，
 * 前端只负责展示，不自己报价。
 */
export function EstimateDialog({
  pending,
  balance,
  submitting,
  onConfirm,
  onClose,
}: EstimateDialogProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const submittingRef = useRef(submitting);
  submittingRef.current = submitting;

  const meta = GENERATION_KIND_META[pending.kind];
  const total = pending.estimate.estimatedPoints;
  const count = pending.shotIds.length;
  const unit = estimatedUnitPoints(pending);
  const insufficient = balance !== null && balance < total;
  const deficit = balance === null ? 0 : total - balance;
  const defaults = formatEstimateDefaults(pending.estimate.defaults);

  useEffect(() => {
    const active = document.activeElement;
    restoreRef.current = active instanceof HTMLElement ? active : null;
    const timer = setTimeout(() => cancelRef.current?.focus(), 0);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape" || submittingRef.current) return;
      event.preventDefault();
      onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      restoreRef.current?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="dp-scrim"
      role="presentation"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        className="dp-dialog"
      >
        <h2 id={titleId} className="font-display text-2xl font-medium text-fg">
          确认{meta.action}
        </h2>
        <p className="mt-1 text-xs text-muted">
          共 {count} 镜 · {meta.unit === "条" ? "按条" : "按张"}计价
        </p>

        <dl className="mt-3 space-y-1.5 rounded-xl border border-border bg-surface-2/60 px-3 py-2 text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-muted">预估点数</dt>
            <dd className="text-base font-semibold tabular-nums text-fg">
              {formatPoints(total)} 点
            </dd>
          </div>
          {unit ? (
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted">单价</dt>
              <dd className="tabular-nums text-fg">
                {formatPoints(unit.points)} 点/{unit.unit}
              </dd>
            </div>
          ) : null}
          {defaults.length > 0 ? (
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted">规格</dt>
              <dd className="tabular-nums text-fg">{defaults.join(" · ")}</dd>
            </div>
          ) : null}
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-muted">当前余额</dt>
            <dd className="tabular-nums text-fg">
              {balance === null ? "未知" : `${formatPoints(balance)} 点`}
            </dd>
          </div>
        </dl>

        {insufficient ? (
          <p role="alert" className="mt-2 text-xs font-medium text-danger">
            点数不足，还差 {formatPoints(deficit)} 点。请充值或减少镜头后再试。
          </p>
        ) : (
          <p className="mt-2 text-[12.5px] text-muted">
            确认后立即预扣预估点数；失败或取消会按契约原路退回，结算以服务端为准。
          </p>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="dp-btn"
          >
            取消
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting || insufficient}
            className="dp-btn primary"
          >
            {submitting ? "提交中…" : "确认生成"}
          </button>
        </div>
      </div>
    </div>
  );
}
